import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { SITE } from "@/lib/site";
import { buildBreadcrumbs } from "@/components/SchemaJsonLd";
import flakeImg from "@/assets/flake-closeup.jpg";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy | MLC Epoxy Floors" },
      { name: "description", content: "How MLC Epoxy Floors collects, uses, and protects your personal information." },
      { property: "og:title", content: "Privacy Policy | MLC Epoxy Floors" },
      { property: "og:description", content: "Our privacy practices, SMS opt-in details, cookie use, and your rights." },
      { property: "og:url", content: "/privacy-policy" },
    ],
    links: [{ rel: "canonical", href: "/privacy-policy" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(buildBreadcrumbs([{ name: "Home", href: "/" }, { name: "Privacy Policy", href: "/privacy-policy" }])) },
    ],
  }),
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        body="How we collect, use, and protect your personal information."
        image={flakeImg}
      />
      <section className="container-x py-20">
        <article className="mx-auto max-w-3xl prose prose-invert prose-headings:font-display prose-headings:uppercase prose-headings:text-silver-light prose-a:text-copper hover:prose-a:text-copper-hover prose-strong:text-silver-light text-silver">
          <p className="text-sm text-muted-foreground">Last updated: June 1, 2026</p>

          <h2>1. Introduction</h2>
          <p>We are committed to protecting the privacy and security of your personal information.</p>
          <p>
            This Privacy Policy describes how MLC Epoxy Floors ("we," "our," or "us") collects, uses, and protects the personal information of individuals who visit our website, contact us, or use our services, including SMS marketing, email marketing, appointment reminders, and website tracking tools. By using our services or opting into our communications, you agree to this Privacy Policy and Terms of Service.
          </p>

          <h2>2. Information We Collect</h2>
          <h3>Contact information</h3>
          <p>We may collect your name, email address, phone number, and mailing address when you submit forms, book appointments, request information, or otherwise provide it to us.</p>
          <h3>Communications data</h3>
          <p>If you opt in, we collect your messaging preferences and maintain records of consent and opt out activity related to SMS and email communications.</p>
          <h3>Usage and device information</h3>
          <p>We may use cookies, pixels, and analytics tools to collect information about how you use our website. This may include your IP address, browser type, pages visited, and interaction data.</p>
          <h3>Appointment and service data</h3>
          <p>If you schedule appointments or use our services, we may collect details associated with those interactions.</p>

          <h2>3. How User Data Is Used</h2>
          <p>We use the information collected for purposes including but not limited to:</p>
          <ul>
            <li>Responding to inquiries and providing requested services</li>
            <li>Sending appointment confirmations and reminders</li>
            <li>Sending transactional and informational messages</li>
            <li>Providing customer support</li>
            <li>Improving website performance and user experience</li>
            <li>Marketing and promotional communications (only where consent is provided)</li>
            <li>Maintaining compliance with legal and regulatory requirements</li>
          </ul>
          <p>We do NOT share, sell, or rent your personal information to any third parties.</p>

          <h2>4. SMS Opt-In Details</h2>
          <p>
            We will only contact you in relation to the services you have opted in for. By agreeing to our Terms of Service, you consent to receive SMS messages from MLC Epoxy Floors, which may include appointment reminders, service updates, and promotional messages. If you wish to withdraw your consent or opt out of future communications, you may do so at any time by contacting us at: <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
          </p>
          <ul>
            <li>Message frequency may vary</li>
            <li>Message &amp; data rates may apply</li>
            <li>Consent is not a condition of purchase</li>
          </ul>
          <p>
            You may opt out of SMS messages at any time by replying STOP. For assistance, reply HELP or contact us at <a href={`mailto:${SITE.email}`}>{SITE.email}</a> or <a href={SITE.phoneHref}>{SITE.phone}</a>.
          </p>

          <h2>5. Cookie &amp; Tracking Practices</h2>
          <p>We use cookies, pixels, and tracking technologies to enhance your browsing experience and analyze website traffic. These technologies may collect information such as:</p>
          <ul>
            <li>Pages visited</li>
            <li>Time spent on the site</li>
            <li>Interaction behavior</li>
          </ul>
          <p>You can control or disable cookies through your browser settings. Disabling cookies may affect certain website features.</p>

          <h2>6. Data Security and Handling</h2>
          <p>We take reasonable administrative, technical, and physical measures to protect your personal information from unauthorized access, disclosure, or misuse. While no system is 100% secure, we follow industry best practices to safeguard your data.</p>
          <p>We retain personal information only as long as necessary to fulfill the purposes outlined in this policy or to comply with legal obligations.</p>

          <h2>7. User Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Request corrections or updates to your information</li>
            <li>Request deletion of your personal data</li>
            <li>Withdraw consent for communications at any time</li>
          </ul>
          <p>To exercise any of these rights, please contact us at: <a href={`mailto:${SITE.email}`}>{SITE.email}</a></p>

          <h2>8. Information Sharing</h2>
          <p>MLC Epoxy Floors does not sell, trade, or rent customer information to any third party. We may share information only when necessary to provide our services, such as processing payments or scheduling appointments, or when required by law, regulation, or legal request. Any third parties we work with are required to handle your data responsibly and in compliance with applicable privacy laws.</p>

          <h2>9. Mobile Information Sharing Statement (A2P Required)</h2>
          <p>No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.</p>
          <p>Information sharing is limited to service providers assisting with messaging delivery and customer communication, all of whom are required to protect your data and use it only for authorized purposes.</p>

          <h2>10. Compliance with A2P 10DLC</h2>
          <p>Our communication practices comply with the A2P 10DLC regulations. We ensure that all messaging is carried out in accordance with the guidelines and requirements set forth by the regulations.</p>

          <h2>11. Third-Party Links</h2>
          <p>Our website may contain links to other websites for your convenience. These third-party sites are not operated or controlled by MLC Epoxy Floors, and we are not responsible for their content or privacy practices. We encourage you to review the privacy policies of any external websites you visit.</p>

          <h2>12. Data Retention</h2>
          <p>We retain your personal information only for as long as necessary to fulfill the purposes described in this policy or as required by law. Once your data is no longer needed, it will be securely deleted or anonymized.</p>

          <h2>13. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time to reflect changes in our business practices, services, or legal requirements. Any updates will be posted on our website with the revised date. Your continued use of our services after such updates indicates your acceptance of the revised policy.</p>

          <h2>14. Contact Us</h2>
          <p>If you have any questions, concerns, or requests regarding this Privacy Policy or how your personal information is handled, please contact us at:</p>
          <p>
            <strong>MLC Epoxy Floors</strong><br />
            {SITE.address.street}, {SITE.address.city}, {SITE.address.region} {SITE.address.postal}<br />
            <a href={SITE.phoneHref}>{SITE.phone}</a><br />
            <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
          </p>
        </article>
      </section>
      <CTASection />
    </>
  );
}
